/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import moment from "moment";
import {
  Accordion,
  AccordionBody,
  AccordionFooter,
  AccordionHeader,
  AccordionItem,
  Button,
  FlexColumn,
  FlexRow,
  Typography,
} from "design-system";
import { CheckIcon } from "design-system/components/Icons";
import ClientConnectPreviewHeader from "./ClientConnectPreviewHeader";
import GettingStartedPreview from "./GettingStartedPreview";
import LeadershipPreview from "./LeadershipPreview";
import SurveySingleCard from "../survey/display-modes/SurveySingleCard";
import { SurveyPreviewProvider } from "../survey/SurveyPreviewContext";
import { questionTypes } from "../survey/SurveyPreviewBody";
import VideoPlaybackModal from "./VideoPlaybackModal";
import { buildBrandThemeStyle } from "../helpers/brandThemeStyle";
import { getClientConnectVisibleQuestions } from "../helpers/clientConnectSurveyQuestions";
import "../client-connect-v2.css";

const STEP_IDS = {
  1: "cc-preview-step-1",
  2: "cc-preview-step-2",
  3: "cc-preview-step-3",
};

function hasGettingStartedContent(introductionVideo, clientContent) {
  const materials = (clientContent || []).filter(
    (item) => item?.file || item?.link,
  );
  return Boolean(introductionVideo) || materials.length > 0;
}

function resolveInitialExpandedStep(introductionVideo, clientContent) {
  return hasGettingStartedContent(introductionVideo, clientContent)
    ? STEP_IDS[1]
    : STEP_IDS[2];
}

export default function ClientConnectV2Content({
  survey,
  brandDetails,
  clientConnect,
  externalAnswers,
  onSetAnswer,
  onSetQuestionError,
  onFinishSurvey,
  onDirectorMessageChange,
  onSubmit,
  onExcludedComplete,
  onIdleSubmitEnabledChange,
  validateSurvey,
  surveyErrors = {},
  isSubmitting = false,
  isExcludedUser = false,
}) {
  const intl = useIntl();
  const isAuditComplete = clientConnect?.userAuditComplete === true;
  const hasLeadership =
    Boolean(survey?.leadershipUser) && !isAuditComplete && !isExcludedUser;

  const introductionVideo = clientConnect?.introduction?.video || null;
  const introductionVideoTitle = clientConnect?.introduction_video_title || "";
  const clientContent = clientConnect?.client_content || [];
  const recipient = clientConnect?.username || "";
  const senderName =
    clientConnect?.user?.name || clientConnect?.user?.email || "Scheduler";

  const hasGettingStartedData = useMemo(
    () => hasGettingStartedContent(introductionVideo, clientContent),
    [introductionVideo, clientContent],
  );

  const activeStepNumbers = useMemo(() => {
    if (isExcludedUser) {
      return hasGettingStartedData ? [1] : [];
    }
    if (isAuditComplete) {
      return hasGettingStartedData ? [1] : [];
    }
    const steps = [];
    if (hasGettingStartedData) {
      steps.push(1);
    }
    steps.push(2);
    if (hasLeadership) {
      steps.push(3);
    }
    return steps;
  }, [hasGettingStartedData, hasLeadership, isAuditComplete, isExcludedUser]);

  const totalSteps = activeStepNumbers.length;

  const getDisplayStepNumber = useCallback(
    (internalStepNum) => {
      const index = activeStepNumbers.indexOf(internalStepNum);
      return index >= 0 ? index + 1 : internalStepNum;
    },
    [activeStepNumbers],
  );

  const surveyQuestions = useMemo(
    () => getClientConnectVisibleQuestions(survey, externalAnswers),
    [survey, externalAnswers],
  );

  const [expandedStepId, setExpandedStepId] = useState(() => {
    if (clientConnect?.userAuditComplete === true || isExcludedUser) {
      return hasGettingStartedContent(introductionVideo, clientContent)
        ? STEP_IDS[1]
        : null;
    }
    return resolveInitialExpandedStep(introductionVideo, clientContent);
  });
  const [completedSteps, setCompletedSteps] = useState({
    1: false,
    2: false,
    3: false,
  });
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [leadershipReply, setLeadershipReply] = useState("");
  const [leadershipReplySaved, setLeadershipReplySaved] = useState(false);

  useEffect(() => {
    if ((isAuditComplete || isExcludedUser) && expandedStepId !== STEP_IDS[1]) {
      setExpandedStepId(hasGettingStartedData ? STEP_IDS[1] : null);
      return;
    }
    if (
      !isExcludedUser &&
      !isAuditComplete &&
      !hasGettingStartedData &&
      expandedStepId === STEP_IDS[1]
    ) {
      setExpandedStepId(STEP_IDS[2]);
    }
  }, [hasGettingStartedData, expandedStepId, isAuditComplete, isExcludedUser]);

  // Idle auto-submit is enabled on the final accordion step only (parity with Client Connect v1
  // final-step idle submit). leadershipReplySaved gates the manual Finish button only; idle submit
  // intentionally does not require Save message before submitting survey answers.
  useEffect(() => {
    if (isAuditComplete || isExcludedUser) {
      onIdleSubmitEnabledChange?.(false);
      return;
    }
    const finalStepId = hasLeadership ? STEP_IDS[3] : STEP_IDS[2];
    onIdleSubmitEnabledChange?.(expandedStepId === finalStepId);
  }, [
    expandedStepId,
    hasLeadership,
    isAuditComplete,
    isExcludedUser,
    onIdleSubmitEnabledChange,
  ]);

  const completedCount = useMemo(
    () => activeStepNumbers.filter((stepNum) => completedSteps[stepNum]).length,
    [activeStepNumbers, completedSteps],
  );
  const hasCompletedSteps = completedCount > 0;

  const currentStep = useMemo(() => {
    let internalStep = 1;
    if (expandedStepId === STEP_IDS[2]) {
      internalStep = 2;
    } else if (expandedStepId === STEP_IDS[3]) {
      internalStep = 3;
    }
    return getDisplayStepNumber(internalStep);
  }, [expandedStepId, getDisplayStepNumber]);

  const accentColor = brandDetails?.primaryColor || undefined;
  const brandThemeStyle = buildBrandThemeStyle(accentColor);

  const handleToggle =
    (stepId, enabled = true) =>
    (nextExpanded) => {
      if (!enabled) {
        return;
      }
      setExpandedStepId(nextExpanded ? stepId : null);
    };

  const handleComplete = useCallback(
    async (stepNum, nextStepId) => {
      const isFinalSubmit = !nextStepId && !isAuditComplete;
      const shouldValidateSurvey =
        validateSurvey && (stepNum === 2 || (isFinalSubmit && stepNum === 3));

      if (shouldValidateSurvey) {
        const hasErrors = validateSurvey();
        if (hasErrors) {
          if (stepNum === 3) {
            setExpandedStepId(STEP_IDS[2]);
          }
          return;
        }
      }

      if (nextStepId) {
        setCompletedSteps((prev) => ({ ...prev, [stepNum]: true }));
        setExpandedStepId(nextStepId);
        return;
      }

      if (isAuditComplete) {
        setCompletedSteps((prev) => ({ ...prev, [stepNum]: true }));
        return;
      }

      if (isExcludedUser) {
        onExcludedComplete?.();
        setCompletedSteps((prev) => ({ ...prev, [stepNum]: true }));
        return;
      }

      if (onSubmit) {
        const success = await onSubmit();
        if (!success) {
          return;
        }
      }

      setCompletedSteps((prev) => ({ ...prev, [stepNum]: true }));
    },
    [
      isAuditComplete,
      isExcludedUser,
      onExcludedComplete,
      onSubmit,
      validateSurvey,
    ],
  );

  const resolveStatus = (stepNum) => {
    if (completedSteps[stepNum]) return "completed";
    if (expandedStepId === STEP_IDS[stepNum]) return "active";
    return "pending";
  };

  const shellStyle = brandThemeStyle;

  const handleLeadershipReplyChange = (value) => {
    setLeadershipReply(value);
    onDirectorMessageChange?.(value);
  };

  const handleLeadershipSaved = (saved) => {
    setLeadershipReplySaved(saved);
    if (saved) {
      onDirectorMessageChange?.(leadershipReply.trim());
    }
  };

  return (
    <SurveyPreviewProvider
      externalAnswers={externalAnswers}
      onSetAnswer={onSetAnswer}
    >
      <FlexColumn
        align="stretch"
        fullWidth
        className="cc-preview-shell"
        style={shellStyle}
      >
        <ClientConnectPreviewHeader
          companyLogo={brandDetails?.logo}
          recipient={recipient}
          senderName={senderName}
          sentDate={moment().format("DD MMM YYYY")}
          currentStep={currentStep}
          totalSteps={totalSteps}
          completedCount={completedCount}
        />

        <div className="cc-preview-shell__scroll">
          <Accordion
            className={[
              "cc-preview-accordion",
              hasCompletedSteps ? "Accordion-completed-items" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {hasGettingStartedData ? (
              <AccordionItem
                id={STEP_IDS[1]}
                status={resolveStatus(1)}
                expanded={expandedStepId === STEP_IDS[1]}
                onToggle={handleToggle(STEP_IDS[1])}
                accentColor={accentColor}
              >
                <AccordionHeader
                  title={intl.formatMessage({
                    id: "client-connect-preview-getting-started",
                  })}
                  description={intl.formatMessage({
                    id: "client-connect-preview-getting-started-desc",
                  })}
                  stepNumber={getDisplayStepNumber(1)}
                  totalSteps={totalSteps}
                  showCompletedStatus={completedSteps[1]}
                />
                <AccordionBody>
                  <GettingStartedPreview
                    introductionVideo={introductionVideo}
                    introductionVideoTitle={introductionVideoTitle}
                    clientContent={clientContent}
                    onOpenVideo={() => setVideoModalOpen(true)}
                    accentColor={accentColor}
                  />
                </AccordionBody>
                <AccordionFooter>
                  {isAuditComplete && !isExcludedUser ? (
                    <Typography
                      component="p"
                      className="cc-preview-audit-complete-note"
                    >
                      {intl.formatMessage({
                        id: "client-connect-preview-already-submitted",
                      })}
                    </Typography>
                  ) : !isAuditComplete ? (
                    <Button
                      type="button"
                      variant="primary"
                      size="base"
                      icon={<CheckIcon size={16} aria-hidden />}
                      onClick={() =>
                        handleComplete(1, isExcludedUser ? null : STEP_IDS[2])
                      }
                    >
                      {intl.formatMessage({
                        id: "client-connect-preview-reviewed-these",
                      })}
                    </Button>
                  ) : null}
                </AccordionFooter>
              </AccordionItem>
            ) : null}

            {!isAuditComplete && !isExcludedUser ? (
              <AccordionItem
                id={STEP_IDS[2]}
                status={resolveStatus(2)}
                expanded={expandedStepId === STEP_IDS[2]}
                onToggle={handleToggle(STEP_IDS[2])}
                accentColor={accentColor}
              >
                <AccordionHeader
                  title={intl.formatMessage({
                    id: "client-connect-preview-share-feedback",
                  })}
                  description={intl.formatMessage({
                    id: "client-connect-preview-share-feedback-desc",
                  })}
                  stepNumber={getDisplayStepNumber(2)}
                  totalSteps={totalSteps}
                  showCompletedStatus={completedSteps[2]}
                />
                <AccordionBody>
                  <FlexColumn
                    align="stretch"
                    fullWidth
                    className="cc-preview-survey-panel"
                  >
                    <Typography
                      variant="headingSmall"
                      component="h3"
                      className="cc-preview-survey-panel__title"
                    >
                      {intl.formatMessage({
                        id: "client-connect-preview-feedback",
                      })}
                    </Typography>
                    <SurveySingleCard
                      step={1}
                      survey={survey}
                      questions={surveyQuestions}
                      recipient={recipient}
                      questionTypes={questionTypes}
                      surveyErrors={surveyErrors}
                      onSetQuestionError={onSetQuestionError}
                      onFinishSurvey={onFinishSurvey}
                      hideSurveyHeader
                    />
                  </FlexColumn>
                </AccordionBody>
                <AccordionFooter>
                  <Button
                    type="button"
                    variant="primary"
                    size="base"
                    icon={<CheckIcon size={16} aria-hidden />}
                    disabled={isSubmitting}
                    onClick={() =>
                      handleComplete(2, hasLeadership ? STEP_IDS[3] : null)
                    }
                  >
                    {intl.formatMessage({
                      id: "client-connect-preview-send-feedback",
                    })}
                  </Button>
                </AccordionFooter>
              </AccordionItem>
            ) : null}

            {!isAuditComplete && !isExcludedUser && hasLeadership ? (
              <AccordionItem
                id={STEP_IDS[3]}
                status={resolveStatus(3)}
                expanded={expandedStepId === STEP_IDS[3]}
                onToggle={handleToggle(STEP_IDS[3])}
                accentColor={accentColor}
              >
                <AccordionHeader
                  title={intl.formatMessage({
                    id: "client-connect-preview-talk-leadership",
                  })}
                  description={intl.formatMessage({
                    id: "client-connect-preview-talk-leadership-desc",
                  })}
                  stepNumber={getDisplayStepNumber(3)}
                  totalSteps={totalSteps}
                  showCompletedStatus={completedSteps[3]}
                />
                <AccordionBody>
                  <LeadershipPreview
                    leadershipUser={survey.leadershipUser}
                    recipient={recipient}
                    reply={leadershipReply}
                    onReplyChange={handleLeadershipReplyChange}
                    replySaved={leadershipReplySaved}
                    onReplySavedChange={handleLeadershipSaved}
                  />
                </AccordionBody>
                <AccordionFooter>
                  {/* Manual finish requires Save message; idle auto-submit on this step does not (see useEffect above). */}
                  <Button
                    type="button"
                    variant="primary"
                    size="base"
                    icon={<CheckIcon size={16} aria-hidden />}
                    disabled={!leadershipReplySaved || isSubmitting}
                    onClick={() => handleComplete(3, null)}
                  >
                    {intl.formatMessage({
                      id: "client-connect-preview-save-finish",
                    })}
                  </Button>
                </AccordionFooter>
              </AccordionItem>
            ) : null}
          </Accordion>

          {isExcludedUser && !hasGettingStartedData ? (
            <FlexRow
              align="center"
              justify="end"
              fullWidth
              className="cc-preview-excluded-finish"
            >
              <Button
                type="button"
                variant="primary"
                size="base"
                onClick={() => onExcludedComplete?.()}
              >
                {intl.formatMessage({ id: "next" })}
              </Button>
            </FlexRow>
          ) : null}
        </div>

        <VideoPlaybackModal
          isOpen={videoModalOpen}
          video={introductionVideo}
          onClose={() => setVideoModalOpen(false)}
        />
      </FlexColumn>
    </SurveyPreviewProvider>
  );
}

ClientConnectV2Content.propTypes = {
  survey: PropTypes.shape({}),
  brandDetails: PropTypes.shape({
    logo: PropTypes.string,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
  }),
  clientConnect: PropTypes.shape({
    userAuditComplete: PropTypes.bool,
  }),
  externalAnswers: PropTypes.object,
  onSetAnswer: PropTypes.func,
  onSetQuestionError: PropTypes.func,
  onFinishSurvey: PropTypes.func,
  onDirectorMessageChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onExcludedComplete: PropTypes.func,
  onIdleSubmitEnabledChange: PropTypes.func,
  validateSurvey: PropTypes.func,
  surveyErrors: PropTypes.object,
  isSubmitting: PropTypes.bool,
  isExcludedUser: PropTypes.bool,
};
